import React, { useState, useEffect, useMemo } from 'react';
import { Sun, Moon, Send, Mic, Volume2, Headphones, X, MicOff, Clock, Hand } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HandoffWeekday, HumanHandoffSchedule } from '../types';
// Single-avatar deployment — operator picks the active HeyGen render
// via the backend's LIVEAVATAR_AVATAR_ID env. The prior
// `getVoiceGender(voiceName)` branch swapped between gendered preview
// PNGs; we now hardcode the path to the active avatar's preview image.

interface WidgetPreviewProps {
  agentName: string;
  companyName: string;
  logoUrlLight: string;
  logoUrlDark: string;
  brandColorLight: string;
  brandColorDark: string;
  welcomeMessage: string;
  launcherType: 'default' | 'custom';
  launcherIconId: string;
  launcherIconUrlLight: string;
  launcherIconUrlDark: string;
  showAiIcon?: boolean;
  aiIconPosition?: 'left' | 'right';
  showAiLabel?: boolean;
  aiLabelText?: string;
  showCustomerIcon?: boolean;
  customerIconPosition?: 'left' | 'right';
  showCustomerLabel?: boolean;
  customerLabelText?: string;
  customerFontColor?: string;
  headerLogoShape?: 'square' | 'rectangle';
  showHeaderTitles?: boolean;
  /** Sub-gate of `showHeaderTitles` — hides the agent-name line only, leaving
   *  the company name as the sole header title. Undefined = shown. */
  showAgentName?: boolean;
  aiIconUrlLight?: string;
  aiIconUrlDark?: string;
  customerIconUrl?: string;
  avatarEnabled?: boolean;
  showChatHistory?: boolean;
  voiceName?: string;
  /** Talk-to-Human gating — mirrors the real widget (Widget.tsx handoff gate).
   *  `handoffEnabled` = the feature itself: off, and neither the button nor the
   *  off-hours notice ever renders. The other two are presentation-only and
   *  INDEPENDENT of each other: `handoffButtonVisible` gates the in-hours "Talk
   *  to a live agent" button, `handoffHoursVisible` gates the off-hours "Our
   *  team is available …" notice. Hiding the button does NOT hide the notice.
   *  Undefined = visible (back-compat), so an agent that never set the flags
   *  previews exactly as it renders live. */
  handoffEnabled?: boolean;
  handoffButtonVisible?: boolean;
  handoffHoursVisible?: boolean;
  /** The agent's staffing schedule, or null for "always available". Availability
   *  itself is computed server-side and is unknowable here, so the preview lets
   *  the operator flip between the in-hours and off-hours states by hand — but
   *  only when a schedule exists, because with 24/7 handoff the off-hours state
   *  is unreachable in production and offering it would be a lie. The label is
   *  formatted the same way the backend formats `human_handoff_hours_label`. */
  handoffSchedule?: HumanHandoffSchedule | null;
}

const HANDOFF_DAY_ORDER: HandoffWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const HANDOFF_DAY_LABELS: Record<HandoffWeekday, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

/** "HH:MM" → "9:00 AM". Mirrors `_fmt_time` in back/utils/handoff_schedule.py. */
const formatHandoffTime = (hhmm: string): string => {
  const [h, m] = (hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
};

/** Preview-side twin of `handoff_hours_label` (back/utils/handoff_schedule.py):
 *  consecutive weekdays sharing a range collapse into one group, groups join
 *  with "; ", timezone in parentheses. Kept in step with the backend by hand —
 *  it only ever feeds the preview, never the live widget, which reads the real
 *  server-rendered label off the config payload. */
const formatHandoffHoursLabel = (schedule?: HumanHandoffSchedule | null): string | undefined => {
  if (!schedule?.days) return undefined;
  const groups: { first: HandoffWeekday; last: HandoffWeekday; start: string; end: string }[] = [];
  HANDOFF_DAY_ORDER.forEach((key, index) => {
    const range = schedule.days[key];
    if (!range?.start || !range?.end) return;
    const prev = groups[groups.length - 1];
    if (prev && prev.start === range.start && prev.end === range.end
      && HANDOFF_DAY_ORDER.indexOf(prev.last) === index - 1) {
      prev.last = key;
    } else {
      groups.push({ first: key, last: key, start: range.start, end: range.end });
    }
  });
  if (groups.length === 0) return undefined;
  const parts = groups.map((g) => {
    const days = g.first === g.last
      ? HANDOFF_DAY_LABELS[g.first]
      : `${HANDOFF_DAY_LABELS[g.first]}–${HANDOFF_DAY_LABELS[g.last]}`;
    return `${days} ${formatHandoffTime(g.start)}–${formatHandoffTime(g.end)}`;
  });
  return `${parts.join('; ')} (${schedule.timezone})`;
};

const getContrastColor = (hexcolor: string) => {
  if (!hexcolor || hexcolor.length < 7) return 'white';
  const hex = hexcolor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const yiq = ((r * 299) + (g * 587) + (114 * b)) / 1000;
  return (yiq >= 128) ? '#111827' : 'white';
};

const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  agentName,
  companyName,
  logoUrlLight,
  logoUrlDark,
  brandColorLight,
  brandColorDark,
  welcomeMessage,
  launcherType,
  launcherIconId,
  launcherIconUrlLight,
  launcherIconUrlDark,
  showAiIcon = true,
  aiIconPosition = 'left',
  showAiLabel = true,
  aiLabelText = 'AI',
  showCustomerIcon = true,
  customerIconPosition = 'left',
  showCustomerLabel = true,
  customerLabelText = 'You',
  customerFontColor = '#ffffff',
  headerLogoShape = 'square',
  showHeaderTitles = true,
  showAgentName = true,
  aiIconUrlLight,
  aiIconUrlDark,
  customerIconUrl,
  avatarEnabled = false,
  showChatHistory = true,
  voiceName,
  handoffEnabled = true,
  handoffButtonVisible = true,
  handoffHoursVisible = true,
  handoffSchedule = null
}) => {
  // voiceName is still threaded through the component for future use
  // (e.g., voice-name display chip), but no longer drives avatar choice.
  void voiceName;
  const avatarImageUrl = '/avatars/allison-preview.png';
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });
  const [isOpen, setIsOpen] = useState(true);
  const [mode, setMode] = useState<'chat' | 'voice'>('chat');
  // Which side of the schedule the preview is showing. The live widget gets
  // this from the server (`human_handoff_available`); here the operator picks,
  // so both states are inspectable at any hour of the day.
  const [previewOffHours, setPreviewOffHours] = useState(false);

  const handoffHoursLabel = useMemo(
    () => formatHandoffHoursLabel(handoffSchedule),
    [handoffSchedule],
  );
  // Only a scheduled agent can ever BE off-hours; 24/7 handoff is always
  // available, so we neither offer the switch nor honour a stale `true`.
  const offHoursReachable = handoffEnabled !== false && !!handoffSchedule;
  const showingOffHours = offHoursReachable && previewOffHours;

  // Sync theme with system preference if it changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const isDark = theme === 'dark';
  const activeBrandColor = isDark ? (brandColorDark || brandColorLight) : brandColorLight;
  const activeLogoUrl = isDark ? (logoUrlDark || logoUrlLight) : (logoUrlLight || logoUrlDark);
  const activeLauncherIconUrl = isDark ? (launcherIconUrlDark || launcherIconUrlLight) : (launcherIconUrlLight || launcherIconUrlDark);
  const activeAiIconUrl = isDark ? (aiIconUrlDark || aiIconUrlLight) : (aiIconUrlLight || aiIconUrlDark);

  const activeTextColor = getContrastColor(activeBrandColor);
  const isLightBg = activeTextColor === '#111827';
  const activeBtnClass = isLightBg ? 'bg-black/10 hover:bg-black/20' : 'bg-white/20 hover:bg-white/30';

  const getLauncherIcon = () => {
    if (launcherType === 'custom' && activeLauncherIconUrl) {
      return <img src={activeLauncherIconUrl} alt="Launcher" className="w-full h-full object-cover rounded-full" />;
    }

    const iconColor = getContrastColor(activeBrandColor);

    if (launcherIconId === 'bot') {
      return (
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="10" rx="2" />
          <circle cx="12" cy="5" r="2" />
          <path d="M12 7v4" />
          <line x1="8" y1="16" x2="8" y2="16" />
          <line x1="16" y1="16" x2="16" y2="16" />
        </svg>
      );
    }
    if (launcherIconId === 'sparkles') {
      return (
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        </svg>
      );
    }
    // Default Message
    return (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-950 rounded-2xl border-2 border-gray-200 dark:border-gray-800 min-h-[550px] w-full max-w-[400px] relative overflow-hidden shadow-inner mx-auto">
      {/* Schedule-state switch — preview chrome, not part of the widget. Sits
          bottom-LEFT: the theme pill owns the top-right, and the top-left would
          cover the widget header's logo and agent name. The bottom strip is
          empty apart from the launcher (bottom-right), so at 375px the two
          never meet. Only rendered when the agent actually has staffing hours
          (see `offHoursReachable`). */}
      {offHoursReachable && (
        <div className="absolute bottom-6 left-6 z-10">
          <button
            type="button"
            onClick={() => setPreviewOffHours(!previewOffHours)}
            aria-pressed={previewOffHours}
            title="Preview how the widget looks inside and outside your staffed hours"
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-full shadow-sm border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Clock className={`w-3.5 h-3.5 flex-shrink-0 ${previewOffHours ? 'text-amber-500 dark:text-amber-400' : 'text-emerald-500 dark:text-emerald-400'}`} />
            <span>{previewOffHours ? 'After hours' : 'In hours'}</span>
          </button>
        </div>
      )}

      {/* External Theme Toggle */}
      <div className="absolute top-4 right-4 flex space-x-2 z-10">
        <button
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          className="flex items-center space-x-2 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-full shadow-sm border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          {isDark ? (
            <>
              <Moon className="w-3.5 h-3.5 text-blue-400" />
              <span>Dark Mode</span>
            </>
          ) : (
            <>
              <Sun className="w-3.5 h-3.5 text-yellow-500" />
              <span>Light Mode</span>
            </>
          )}
        </button>
      </div>

      {/* Chat Window Preview */}
      <div className={`w-[320px] h-[450px] shadow-2xl rounded-2xl overflow-hidden flex flex-col transition-all duration-300 mb-20 ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-10 pointer-events-none'} ${isDark ? 'bg-black border border-gray-800' : 'bg-white border border-gray-100'}`}>
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between transition-colors duration-300"
          style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
        >
          <div className="flex items-center space-x-2">
            {activeLogoUrl ? (
              <img
                src={activeLogoUrl}
                alt="Logo"
                className={headerLogoShape === 'rectangle' ? 'h-8 max-w-[150px] object-contain' : 'w-8 h-8 rounded-full object-cover'}
              />
            ) : (
              <div className={`${headerLogoShape === 'rectangle' ? 'h-8 max-w-[150px]' : 'w-8 h-8 rounded-full p-1'} flex items-center justify-center overflow-hidden ${isLightBg && headerLogoShape !== 'rectangle' ? 'bg-black/10' : ''} ${!isLightBg && headerLogoShape !== 'rectangle' ? 'bg-white/20' : ''}`}>
                <img src={headerLogoShape === 'rectangle' ? (isLightBg ? "/logo-purple.png" : "/logo-white.png") : (isLightBg ? "/logo-only-purple.png" : "/logo-only-white.png")} alt="Default" className="w-full h-full object-contain" />
              </div>
            )}
            {showHeaderTitles && (
              <div>
                {showAgentName && (
                  <h4 className="font-semibold text-sm leading-tight truncate max-w-[120px]">{agentName || 'Support Agent'}</h4>
                )}
                <p className={showAgentName ? 'text-[10px] opacity-80 truncate max-w-[120px]' : 'font-semibold text-sm leading-tight truncate max-w-[120px]'}>{companyName || 'AskProdigy AI'}</p>
              </div>
            )}
          </div>
          <div className="flex space-x-1">
            <button
              onClick={(e) => { e.stopPropagation(); setTheme(isDark ? 'light' : 'dark'); }}
              className={`p-1.5 rounded-full transition-colors ${activeBtnClass}`}
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            {showChatHistory && (
              <button
                onClick={(e) => e.stopPropagation()}
                className={`p-1.5 rounded-full transition-colors ${activeBtnClass}`}
                title="Chat History"
              >
                <Clock className="w-3.5 h-3.5" />
              </button>
            )}
            {mode === 'chat' ? (
              <button
                onClick={(e) => { e.stopPropagation(); setMode('voice'); }}
                className={`p-1.5 rounded-full transition-colors ${activeBtnClass}`}
                title="Switch to Live Voice"
              >
                <Headphones className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setMode('chat'); }}
                className={`p-1.5 rounded-full transition-colors ${activeBtnClass}`}
                title="Return to Chat"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {mode === 'chat' ? (
          <>
            {/* Messages */}
            <div className={`flex-1 p-3 space-y-3 overflow-y-auto ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
              <div className="flex justify-start">
                <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-100 text-gray-900'}`}>
                  <div
                    className={`flex items-start ${
                      aiIconPosition === 'right' ? 'gap-1' : 'gap-2'
                    }`}
                  >
                    {/* AI Icon BEFORE text when left-positioned */}
                    {showAiIcon && aiIconPosition !== 'right' && (
                      <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
                        {activeAiIconUrl ? (
                          <img src={activeAiIconUrl} alt="AI" className="w-full h-full object-cover" />
                        ) : (
                          <span className={`text-[8px] font-bold ${isDark ? 'text-gray-300' : 'text-blue-600'}`}>AI</span>
                        )}
                      </span>
                    )}
                    <div className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`} style={{ color: 'inherit' }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => (
                            <p>
                              {showAiLabel && aiLabelText && <strong className="mr-1">{aiLabelText}:</strong>}
                              {children}
                            </p>
                          )
                        }}
                      >
                        {welcomeMessage || 'How can I help you today?'}
                      </ReactMarkdown>
                    </div>
                    {/* AI Icon AFTER text when right-positioned */}
                    {showAiIcon && aiIconPosition === 'right' && (
                      <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
                        {activeAiIconUrl ? (
                          <img src={activeAiIconUrl} alt="AI" className="w-full h-full object-cover" />
                        ) : (
                          <span className={`text-[8px] font-bold ${isDark ? 'text-gray-300' : 'text-blue-600'}`}>AI</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Sample Message */}
              <div className="flex justify-end mt-4">
                <div
                  className="max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow-sm"
                  style={isDark
                    ? { backgroundColor: 'white', color: '#111827', border: `2px solid ${activeBrandColor}` }
                    : { backgroundColor: activeBrandColor, color: customerFontColor }
                  }
                >
                  <div
                    className={`flex items-start ${
                      customerIconPosition === 'right' ? 'gap-1' : 'gap-2'
                    }`}
                  >
                    {/* Customer Icon BEFORE text when left-positioned */}
                    {showCustomerIcon && customerIconPosition !== 'right' && (
                      <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${isDark ? 'bg-gray-200' : 'bg-white'}`}>
                        {customerIconUrl ? (
                          <img src={customerIconUrl} alt="User" className="w-full h-full object-cover" />
                        ) : (
                          <span className={`text-[8px] font-bold text-gray-700`}>U</span>
                        )}
                      </span>
                    )}
                    <div className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`} style={{ color: 'inherit' }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => (
                            <p>
                              {showCustomerLabel && customerLabelText && <strong className="mr-1" style={{ color: 'inherit' }}>{customerLabelText}:</strong>}
                              {children}
                            </p>
                          )
                        }}
                      >
                        Hello there, I have some payment issues.
                      </ReactMarkdown>
                    </div>
                    {/* Customer Icon AFTER text when right-positioned */}
                    {showCustomerIcon && customerIconPosition === 'right' && (
                      <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${isDark ? 'bg-gray-200' : 'bg-white'}`}>
                        {customerIconUrl ? (
                          <img src={customerIconUrl} alt="User" className="w-full h-full object-cover" />
                        ) : (
                          <span className={`text-[8px] font-bold text-gray-700`}>U</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Talk-to-Human footer — static (non-interactive) mirror of the live
                widget's gate in Widget.tsx. Same three flags, same INDEPENDENT
                semantics: `handoffEnabled` off kills both branches; in-hours
                shows the button unless `handoffButtonVisible` is false;
                off-hours shows the notice unless `handoffHoursVisible` is false.
                Hiding the button does NOT hide the notice — they answer
                different questions. Which side of the schedule we are on is the
                operator's choice here (`showingOffHours`), because real
                availability is computed server-side. `!== false` (not the param
                default) so an explicit `null` from the API still reads as ON,
                matching the widget. Suppressed → `null`, never an empty
                bordered div. */}
            {handoffEnabled !== false && (
              !showingOffHours ? (
                handoffButtonVisible !== false ? (
                  <div className={`px-3 pt-2 pb-1 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`} style={{ backgroundColor: activeBrandColor }}>
                    <div
                      className="w-full flex items-center justify-center gap-2 text-xs font-medium py-1.5 rounded-full bg-white/15"
                      style={{ color: activeTextColor }}
                      title="Talk to a live agent"
                    >
                      <Hand size={14} />
                      <span>Talk to a live agent</span>
                    </div>
                  </div>
                ) : null
              ) : handoffHoursVisible !== false ? (
                <div className={`px-3 pt-2 pb-1 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`} style={{ backgroundColor: activeBrandColor }}>
                  <div
                    className="w-full flex items-center justify-center gap-1.5 text-[11px] font-medium leading-snug text-center py-1"
                    style={{ color: activeTextColor, opacity: 0.9 }}
                  >
                    <Clock size={12} className="flex-shrink-0" />
                    <span>
                      {handoffHoursLabel
                        ? `Our team is available ${handoffHoursLabel}. You can keep chatting with our AI assistant here — it can raise a support ticket for the team whenever you need one.`
                        : `Our team is offline right now. You can keep chatting with our AI assistant here — it can raise a support ticket for the team whenever you need one.`}
                    </span>
                  </div>
                </div>
              ) : null
            )}

            {/* Input Area */}
            <div className="p-3 border-t border-transparent" style={{ backgroundColor: activeBrandColor }}>
              <div className="flex items-center space-x-2 mb-2">
                <div className="p-1.5" style={{ color: activeTextColor, opacity: 0.7 }}>
                  <Volume2 size={16} />
                </div>
                <div className="p-1.5" style={{ color: activeTextColor, opacity: 0.7 }}>
                  <Mic size={16} />
                </div>
                <div className={`flex-1 h-8 rounded-full border text-[10px] px-3 flex items-center ${isDark ? 'bg-gray-800 border border-gray-700 text-gray-500' : 'bg-white border border-gray-200 text-gray-400'}`}>
                  Message...
                </div>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300"
                  style={{ backgroundColor: activeTextColor, color: activeBrandColor }}
                >
                  <Send size={14} />
                </div>
              </div>
              <div className="text-center" style={{ color: activeTextColor, opacity: 0.7 }}>
                <span className="text-[9px]">Powered by AskProdigy AI</span>
              </div>
            </div>
          </>
        ) : (
          <div className={`flex-1 flex flex-col items-center justify-center p-6 space-y-6 ${isDark ? 'bg-black' : 'bg-white'}`}>
            <div className="text-center space-y-1">
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Realtime Voice Agent</h3>
              <p className="text-[10px] text-gray-500">Status: Ready</p>
            </div>

            {avatarEnabled ? (
              <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-md">
                <img
                  src={avatarImageUrl}
                  alt="AI Avatar preview"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className={`w-24 h-24 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <MicOff className="w-8 h-8 text-gray-400" />
              </div>
            )}

            <button
              className="w-full py-3 rounded-full text-white text-sm font-semibold shadow-lg transition-transform hover:scale-105"
              style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
            >
              Start Conversation
            </button>

            <div className="text-center">
              <span className="text-[9px] text-gray-400 block mb-2">Ensure you are in a quiet environment.</span>
              <span className="text-[9px] text-gray-400">Powered by AskProdigy AI</span>
            </div>
          </div>
        )}
      </div>

      {/* Launcher Button Preview */}
      <div
        className="absolute bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all hover:scale-110 active:scale-95 duration-300"
        style={{ backgroundColor: activeBrandColor }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <X className="w-6 h-6" style={{ color: activeTextColor }} />
        ) : (
          getLauncherIcon()
        )}
      </div>

      <div className="absolute bottom-4 left-4 flex flex-col">
        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
          Live Preview
        </span>
        <span className="text-[9px] text-gray-400">
          Click the launcher to toggle
        </span>
      </div>
    </div>
  );
};

export default WidgetPreview;

